// eslint-disable-next-line import/no-extraneous-dependencies
import * as amplitude from '@amplitude/analytics-browser';
import { routes } from '@constants';
import {
  useGetDownloadedAvatar,
  useNavigateWithOfflineMode,
  // useNotifications,
  useScreenSize,
} from '@hooks';
import { authApi, authSlice, userApi } from '@store';
import { Button, Flex, Image, theme, Typography } from '@styles';
import { ReactComponent as Burger } from '@svgs/burger.svg';
import { ReactComponent as DropdownIcon } from '@svgs/chevron-down.svg';
import { ReactComponent as LogoEN } from '@svgs/logo-outlined-en.svg';
import { ReactComponent as LogoUA } from '@svgs/logo-outlined-ua.svg';
import { ReactComponent as PasskeyIcon } from '@svgs/passkey.svg';
import { func, object } from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { useOnlineStatusDetector } from '../../hooks/useOnlineStatusDetector';
import { Dropdown } from '../Dropdown';
import { Icon } from '../Icon';
import {
  dropdownLanguageItems,
  getDropdownProfileItems,
  navBarItemsProps,
} from './Header.layoutProps';

const Header = ({ containerProps, logInButtonClick, onBurgerClick }) => {
  const location = useLocation();
  const { navigate } = useNavigateWithOfflineMode();
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const { isLaptop, isTabletLG } = useScreenSize();
  const { isOnline } = useOnlineStatusDetector();
  const languageRef = useRef();
  const profileRef = useRef();

  const { jwtAuthenticationToken, language } = useSelector(
    (state) => state.authSlice
  );
  const downloadedAvatar = useGetDownloadedAvatar();

  const [isLanguageDropdownVisible, setLanguageDropdownVisible] =
    useState(false);
  const [isProfileDropdownVisible, setProfileDropdownVisible] = useState(false);

  const { data } = userApi.useGetUserQuery(undefined, {
    skip: !jwtAuthenticationToken || !isOnline,
  });
  const [signOut] = authApi.useSignOutMutation();

  // const { notifications, refetchNotifications } = useNotifications();

  useEffect(() => {
    dispatch(authSlice.actions.setOnboardingStatus(data?.data?.is_onboarded));
    dispatch(authSlice.actions.setAvatar(data?.data?.avatar));
  }, [data]);

  const handleNavigateHome = () => {
    navigate('/');
  };

  const handleClickTab = (route) => () => {
    // if (type !== 'activity') {
    //   dispatch(activitiesSlice.actions.readNotifications(type));
    // }
    // refetchNotifications();
    navigate(route);
  };

  const handleLanguageDropdown = () => {
    setProfileDropdownVisible(false);
    setLanguageDropdownVisible((prevState) => !prevState);
  };

  const handleProfileDropdown = () => {
    setLanguageDropdownVisible(false);
    setProfileDropdownVisible((prevState) => !prevState);
  };

  const handleProfileDropdownItemClick = (dropdownItemId) => async () => {
    setProfileDropdownVisible(false);

    if (dropdownItemId === 'signOut') {
      navigate(routes.mainPage);

      await signOut().unwrap();
      amplitude.reset();
      dispatch(authSlice.actions.signOut());

      return;
    }

    navigate(dropdownItemId);
  };

  const handleLanguageDropdownItemClick = (dropdownItemId) => () => {
    setLanguageDropdownVisible(false);
    dispatch(authSlice.actions.setLanguage(dropdownItemId));

    i18n.changeLanguage(dropdownItemId);
  };

  // const checkNotifications = (type) => {
  //   switch (type) {
  //     case 'activity':
  //       return (
  //         notifications?.scenario ||
  //         notifications?.card_form_activity ||
  //         notifications?.extra_test
  //       );
  //     default:
  //       return notifications?.[type];
  //   }
  // };

  const LogoIcon = {
    en: LogoEN,
    ua: LogoUA,
  };

  const navBarItems = useMemo(() =>
    navBarItemsProps.map((navBarItem, index) => (
      <Flex
        key={`${index}-${navBarItem.id}`}
        pb={37}
        pt={location.pathname === navBarItem.route ? 33 : 37}
        {...(location.pathname === navBarItem.route && {
          borderTop: `4px solid ${theme.colors.schoolBusYellow[100]}`,
        })}
        alignItems="center"
      >
        {!jwtAuthenticationToken && navBarItem.disableIfUnauthorized ? (
          <Button
            height="fit-content"
            onClick={logInButtonClick}
            variant="textButton"
          >
            <Typography
              color={theme.colors.white[100]}
              textAlign="center"
              variant="MS-semibold-s16-lh17"
            >
              {i18n.t(navBarItem.titleText).toUpperCase()}
            </Typography>
          </Button>
        ) : (
          <Button
            key={navBarItem.id}
            onClick={handleClickTab(navBarItem.route)}
            variant="textButton"
          >
            <Typography
              color={theme.colors.white[100]}
              position="relative"
              textAlign="center"
              variant="MS-semibold-s16-lh17"
            >
              {/* {checkNotifications(navBarItem.type) && (
                <Flex
                  backgroundColor={theme.colors.schoolBusYellow[100]}
                  border={`0.5px solid ${theme.colors.black[100]}`}
                  borderRadius={6}
                  height={11}
                  position="absolute"
                  right={-6}
                  top={-6}
                  width={11}
                  zIndex={100}
                />
              )} */}
              {i18n.t(navBarItem.titleText).toUpperCase()}
            </Typography>
          </Button>
        )}
      </Flex>
    ))
  );

  return (
    <Flex
      alignItems="center"
      flexDirection="row"
      justifyContent="space-between"
      {...containerProps}
    >
      <Flex gap={containerProps.innerFlexGap || 48}>
        <Icon
          as={Button}
          height={isTabletLG ? 69 : 131}
          mr={!isLaptop && 38}
          onClick={handleNavigateHome}
          p={0}
          SVG={LogoIcon[language || 'en']}
          variant="textButton"
          width={isTabletLG ? 52 : 98}
        />
        {!isTabletLG && navBarItems}
      </Flex>
      <Flex alignItems="center" gap={containerProps.buttonFlexGap}>
        {!isTabletLG && isOnline && (
          <>
            <Button
              ref={languageRef}
              onClick={handleLanguageDropdown}
              variant="textButton"
            >
              <Typography
                color={theme.colors.white}
                mr={1}
                variant="MS-semibold-s16-lh17"
              >
                {language?.toUpperCase()}
              </Typography>
              <Icon
                height={24}
                style={{
                  transform: isLanguageDropdownVisible ? 'rotate(180deg)' : '',
                  transition: '0.7s',
                }}
                SVG={DropdownIcon}
                width={24}
              />
            </Button>
            <Dropdown
              containerProps={{ top: 110 }}
              excludedRef={languageRef}
              isVisible={isLanguageDropdownVisible}
              items={dropdownLanguageItems}
              onClick={handleLanguageDropdownItemClick}
              setIsVisible={setLanguageDropdownVisible}
            />
          </>
        )}
        {jwtAuthenticationToken ? (
          <>
            <Button
              ref={profileRef}
              onClick={handleProfileDropdown}
              variant="textButton"
            >
              {(isOnline ? data?.data.avatar : downloadedAvatar) && (
                <Image
                  alt="Profile picture"
                  borderRadius={28}
                  height={isTabletLG ? 48 : 56}
                  minWidth={isTabletLG ? 48 : 56}
                  src={isOnline ? data?.data.avatar : downloadedAvatar}
                  width={isTabletLG ? 48 : 56}
                />
              )}
            </Button>
            <Dropdown
              containerProps={!isTabletLG && { top: 110, zIndex: 100 }}
              excludedRef={profileRef}
              isVisible={isProfileDropdownVisible}
              items={getDropdownProfileItems({ t: i18n.t })}
              onClick={handleProfileDropdownItemClick}
              setIsVisible={setProfileDropdownVisible}
              userHeader={{
                avatar: isOnline ? data?.data.avatar : downloadedAvatar,
                username: data?.data.username,
              }}
            />
            {isTabletLG && (
              <Button onClick={onBurgerClick} variant="textButton">
                <Icon height={14} SVG={Burger} width={30} />
              </Button>
            )}
          </>
        ) : (
          <Button
            onClick={logInButtonClick}
            px={24}
            variant="primary"
            {...(isTabletLG && { height: '38px' })}
            whiteSpace="nowrap"
          >
            <Icon color={theme.colors.black[100]} mr={1} SVG={PasskeyIcon} />
            <Typography
              color={theme.colors.black[100]}
              variant={
                isTabletLG ? 'MS-semibold-s14-lh15' : 'MS-semibold-s16-lh17'
              }
            >
              {i18n.t('Footer.Login')?.toUpperCase()}
            </Typography>
          </Button>
        )}
      </Flex>

      {isTabletLG && !jwtAuthenticationToken && (
        <Button onClick={onBurgerClick} variant="textButton">
          <Icon height={14} SVG={Burger} width={30} />
        </Button>
      )}
    </Flex>
  );
};

Header.propTypes = {
  containerProps: object,
  logInButtonClick: func,
  onAvatarButtonClick: func,
  onBurgerClick: func.isRequired,
};

Header.defaultProps = {
  containerProps: {},
  logInButtonClick: () => {},
  onAvatarButtonClick: () => {},
};

export default Header;
